/* eslint-disable ember/no-runloop */
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PRISM_SPACING_1000 } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelRatesTooltipsShortTermRentalTooltip from 'lumen/components/hotel/rates/tooltips/short-term-rental-tooltip';
import { GROUP } from 'lumen/utils/constants/chart-color-constants';
import { HIGHCHARTS_MISSING_MARKER } from 'lumen/utils/constants/general-constants';
import {
  SHORT_TERM_RENTAL_OTB,
  SHORT_TERM_RENTAL_RATE,
} from 'lumen/utils/constants/short-term-rental-constants';

import styles from './chart-short-term-rental-evolution-day.module.css';

interface HotelShortTermRentalChartsChartShortTermRentalEvolutionDaySignature {
  Args: {
    onUpdateChartReference: unknown;
    refDaysAgo: unknown;
    sortedData: unknown;
  };
}

export default class HotelShortTermRentalChartsChartShortTermRentalEvolutionDay extends Component<HotelShortTermRentalChartsChartShortTermRentalEvolutionDaySignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // @ts-expect-error: Incorrect type
  @tracked leadTime;
  // @ts-expect-error: Incorrect type
  @tracked occupancy;
  // @ts-expect-error: Incorrect type
  @tracked period;
  // @ts-expect-error: Incorrect type
  @tracked rate;
  // @ts-expect-error: Incorrect type
  @tracked rentalOccupancy;
  // @ts-expect-error: Incorrect type
  @tracked rentalRate;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const chartData = [];

    const rateSeries = {
      name: this.intl.t('short_term_rental.my_rate'),
      data: [],
      type: 'line',
      color: 'var(--prism-color-chart-categorical-01)',
      fillOpacity: 0.15,
      borderWidth: 1,
      marker: { enabled: true, radius: 4 },
    };

    const otbSeries = {
      name: this.intl.t('rates.my_OTB'),
      data: [],
      type: 'line',
      yAxis: 1,
      color: GROUP,
      fillOpacity: 0.15,
      borderWidth: 1,
      marker: { enabled: false },
    };

    const rentalRateSeries = {
      name: this.intl.t('alternative_lodging.rate'),
      data: [],
      type: 'line',
      color: SHORT_TERM_RENTAL_RATE,
      fillOpacity: 0.15,
      borderWidth: 1,
      marker: { enabled: true, radius: 4 },
    };

    const rentalOTBSeries = {
      name: this.intl.t('alternative_lodging.otb'),
      data: [],
      type: 'line',
      yAxis: 1,
      color: SHORT_TERM_RENTAL_OTB,
      borderColor: 'var(--prism-color-border-inverse-emphasis)',
      fillOpacity: 0.15,
      borderWidth: 1,
      marker: { enabled: false },
    };

    const marketOtbSeries = {
      name: this.intl.t('rates.tooltip.market_otb'),
      data: [],
      type: 'line',
      yAxis: 1,
      color: 'var(--prism-color-chart-accent-secondary-figure)',
      fillOpacity: 0.15,
      borderWidth: 1,
      marker: { enabled: false },
    };

    // @ts-expect-error: Incorrect type
    let previousOwnHotelRate = null;
    // @ts-expect-error: Incorrect type
    let previousRentalRate = null;
    // @ts-expect-error: Incorrect type
    Object.keys(this.args.sortedData).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        period,
        occupancy,
        rentalRate,
        rentalOccupancy,
        events,
        holidays,
        // @ts-expect-error: Incorrect type
      } = this.args.sortedData[key];
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const rate = period.rates[this.currentHotel.hotelinfo.id][0];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const x = period.extractDate.utc(true).unix() * 1000;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const leadTime = period.leadtime;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isOwnHotelRateMissing = rate.value === 0;
      // @ts-expect-error: Incorrect type
      rateSeries.data.push({
        x,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        y: isOwnHotelRateMissing ? previousOwnHotelRate : rate.value,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        occupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalRate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalOccupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        events,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        holidays,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        leadTime,
        marker: isOwnHotelRateMissing ? HIGHCHARTS_MISSING_MARKER : null,
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      previousOwnHotelRate = isOwnHotelRateMissing
        ? // @ts-expect-error: Incorrect type
          previousOwnHotelRate
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rate.value;

      if (this.access.hasOTB) {
        // @ts-expect-error: Incorrect type
        otbSeries.data.push({
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: occupancy.occupancyPercentage || null, // Don't show missing OTB data
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          period,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          occupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rentalRate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rentalOccupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          events,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          holidays,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          leadTime,
        });
      }

      if (this.access.hasMarketOtb) {
        // @ts-expect-error: Incorrect type
        marketOtbSeries.data.push({
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: occupancy.benchmarkValue || null, // Don't show missing OTB data
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          period,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          occupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rentalRate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rentalOccupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          events,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          holidays,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          leadTime,
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isRentalRateMissing = rentalRate.value === 0;
      // @ts-expect-error: Incorrect type
      rentalRateSeries.data.push({
        x,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        y: isRentalRateMissing ? previousRentalRate : rentalRate.value,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        occupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalRate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalOccupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        events,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        holidays,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        leadTime,
        marker: isRentalRateMissing ? HIGHCHARTS_MISSING_MARKER : null,
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      previousRentalRate = isRentalRateMissing
        ? // @ts-expect-error: Incorrect type
          previousRentalRate
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rentalRate.value;

      // @ts-expect-error: Incorrect type
      rentalOTBSeries.data.push({
        x,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        y: rentalOccupancy.value || null, // Don't show missing OTB data
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        occupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalRate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalOccupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        events,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        holidays,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        leadTime,
      });
    });

    if (this.access.hasOTB) {
      chartData.push(otbSeries);
    }
    if (this.access.hasMarketOtb) {
      chartData.push(marketOtbSeries);
    }
    chartData.push(rentalOTBSeries);
    chartData.push(rateSeries);
    chartData.push(rentalRateSeries);

    return chartData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      chart: {
        type: 'line',
        marginTop: PRISM_SPACING_1000,
      },
      rangeSelector: {
        selected: 1,
      },
      scrollbar: {
        enabled: false,
        liveRedraw: false,
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        enabled: true,
        borderWidth: 0,
        itemStyle: {
          color: 'var(--prism-color-text-neutral-subdued)',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      plotOptions: {
        column: {
          pointWidth: 10,
        },
        series: {
          connectNulls: true,
          dataLabels: {
            y: -25,
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      xAxis: {
        type: 'datetime',
        lineColor: 'var(--prism-color-border-neutral-default)',
        crosshair: true,
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            return dayjs.utc(this.value).format('ll');
          },
          y: 25,
        },
      },
      yAxis: [
        {
          title: {
            text: this.intl.t('general.rate'),
          },
          labels: {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // @ts-expect-error: Incorrect type
              return `${that.currentHotel.currencySymbol} ${this.value}`;
            },
          },
        },
        {
          gridLineWidth: 0,
          opposite: true,
          title: {
            text: this.intl.t('general.occupancy'),
          },
          labels: {
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              return that.intl.formatNumber(this.value, {
                style: 'percent',
              });
            },
          },
          showFirstLabel: false,
        },
      ],
      tooltip: {
        hideDelay: 0.2,
        crosshairs: true,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        distance: 20,
        shared: true,
        outside: true,

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const {
            period,
            rate,
            occupancy,
            rentalRate,
            rentalOccupancy,
            leadTime,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          } = this.points[0].point;
          begin();
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.period = period;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.rate = rate;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.occupancy = occupancy;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.rentalOccupancy = rentalOccupancy;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.rentalRate = rentalRate;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.leadTime = leadTime;
          end();
          // @ts-expect-error: Incorrect type
          return document.getElementById(
            'dummy-tooltip-for-short-term-rental-evolution',
          ).innerHTML;
        },
        useHTML: true,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentHotel() {
    return this.hotelAndMarketService.hotel;
  }

  <template>
    <GeneralChartsOtaChart
      {{! @glint-expect-error: Incorrect type }}
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      {{! @glint-expect-error: Incorrect type }}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />

    <div class="hidden" id="dummy-tooltip-for-short-term-rental-evolution">
      <div class={{styles.tooltip-short-term-rental}}>
        {{#if this.period}}
          <HotelRatesTooltipsShortTermRentalTooltip
            {{! @glint-expect-error: Incorrect type }}
            @events={{this.events}}
            @hasShortTermRentalAlert={{false}}
            {{! @glint-expect-error: Incorrect type }}
            @holidays={{this.holidays}}
            @leadTime={{this.leadTime}}
            @occupancy={{this.occupancy}}
            @period={{this.period}}
            {{! @glint-expect-error: Incorrect type }}
            @refDaysAgo={{@refDaysAgo}}
            @rentalOccupancy={{this.rentalOccupancy}}
            @rentalRate={{this.rentalRate}}
            @showChanges={{false}}
            @showEventsAndHolidays={{false}}
            @showLeadtime={{true}}
          />
        {{/if}}
      </div>
    </div>
  </template>
}
